import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
} from "@/components/ui/Dialog";
import Button from "@/components/ui/Button";
import {
  AssetView,
  AssetCondition,
  AssetRequestView,
  AssetRequestPriority,
  AssetStatus,
  FulfillAssetRequestInput,
  getAssets,
  generateOtp,
  fulfillAssetRequest,
} from "../../../../api/General/assets";

interface FulfillAssetRequestModalProps {
  open: boolean;
  onClose: () => void;
  request: AssetRequestView | null;
  onFulfilled: () => void;
  requireOtp: boolean;
}

const priorityStyles: Record<string, string> = {
  [AssetRequestPriority.Low]: "bg-blue-100 text-blue-800",
  [AssetRequestPriority.Medium]: "bg-orange-100 text-orange-800",
  [AssetRequestPriority.High]: "bg-red-100 text-red-800",
};

const priorityI18nKeys: Record<string, string> = {
  [AssetRequestPriority.Low]: "assetRequests.low",
  [AssetRequestPriority.Medium]: "assetRequests.medium",
  [AssetRequestPriority.High]: "assetRequests.high",
};

const FulfillAssetRequestModal: React.FC<FulfillAssetRequestModalProps> = ({
  open,
  onClose,
  request,
  onFulfilled,
  requireOtp,
}) => {
  const { t } = useTranslation();

  const [availableAssets, setAvailableAssets] = useState<AssetView[]>([]);
  const [loadingAssets, setLoadingAssets] = useState(false);
  const [selectedAssetId, setSelectedAssetId] = useState<number | null>(null);
  const [condition, setCondition] = useState<AssetCondition>(AssetCondition.Good);
  const [notes, setNotes] = useState("");
  const [otp, setOtp] = useState("");
  const [otpGenerated, setOtpGenerated] = useState(false);
  const [generatingOtp, setGeneratingOtp] = useState(false);
  const [fulfilling, setFulfilling] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const loadAvailableAssets = useCallback(async () => {
    if (!request) return;
    setLoadingAssets(true);
    try {
      const result = await getAssets({
        categoryId: request.categoryId,
        status: AssetStatus.Available,
        page: 1,
        pageSize: 100,
      });
      if (result.errors.length === 0) {
        setAvailableAssets(result.payload.items);
      }
    } catch {
      // silent
    } finally {
      setLoadingAssets(false);
    }
  }, [request]);

  // Reset form on open
  useEffect(() => {
    if (request && open) {
      setSelectedAssetId(null);
      setCondition(AssetCondition.Good);
      setNotes("");
      setOtp("");
      setOtpGenerated(false);
      setErrors({});
      loadAvailableAssets();
    }
  }, [request, open, loadAvailableAssets]);

  const handleGenerateOtp = useCallback(async () => {
    if (!request || selectedAssetId == null) return;
    setGeneratingOtp(true);
    try {
      const result = await generateOtp(selectedAssetId, request.employeeId);
      if (result.errors.length === 0) {
        setOtpGenerated(true);
      }
    } catch {
      // silent
    } finally {
      setGeneratingOtp(false);
    }
  }, [request, selectedAssetId]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (selectedAssetId == null) newErrors.asset = t("assetRequests.selectAsset");
    if (requireOtp) {
      if (!otp.trim()) newErrors.otp = t("assets.validation.otpRequired");
      else if (otp.length !== 6) newErrors.otp = t("assets.validation.otpLength");
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate() || !request || selectedAssetId == null) return;
    setFulfilling(true);
    try {
      const input: FulfillAssetRequestInput = {
        requestId: request.id,
        assetId: selectedAssetId,
        condition,
        notes: notes || undefined,
        otp: requireOtp ? otp : "",
      };
      const result = await fulfillAssetRequest(input);
      if (result.errors.length === 0) {
        onFulfilled();
        onClose();
      } else {
        setErrors((prev) => ({ ...prev, otp: result.errors[0]?.description || t("assets.invalidOtp") }));
      }
    } catch (err: any) {
      setErrors((prev) => ({ ...prev, otp: err?.errors?.[0]?.description || t("assets.invalidOtp") }));
    } finally {
      setFulfilling(false);
    }
  };

  if (!request) return null;

  const conditionLabel = (c: AssetCondition) => {
    switch (c) {
      case AssetCondition.Excellent: return t("assets.conditionExcellent");
      case AssetCondition.Good: return t("assets.conditionGood");
      case AssetCondition.Fair: return t("assets.conditionFair");
      case AssetCondition.Poor: return t("assets.conditionPoor");
      default: return "";
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !fulfilling) onClose(); }}>
      <DialogContent size="lg">
        <DialogHeader className="sticky top-0 bg-card z-10">
          <DialogTitle>{t("assetRequests.fulfillRequest")}</DialogTitle>
          <p className="text-sm text-muted-foreground mt-1">
            {t("assetRequests.fulfillDescription")}
          </p>
        </DialogHeader>

        <form id="fulfill-form" onSubmit={handleSubmit}>
          <DialogBody className="space-y-6">
            {/* Request Information */}
            <div className="bg-muted rounded-lg p-4">
              <h3 className="text-sm font-medium text-foreground mb-3">
                {t("assetRequests.requestInfo")}
              </h3>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-muted-foreground">{t("assetRequests.requestedBy")}</span>
                  <p className="font-medium">{request.employeeName || "-"}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">{t("assetRequests.department")}</span>
                  <p className="font-medium">{request.department || "-"}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">{t("assetRequests.requestedCategory")}</span>
                  <p className="font-medium">{request.categoryName || "-"}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">{t("assetRequests.reason")}</span>
                  <p className="font-medium">{request.reason || "-"}</p>
                </div>
              </div>
              <div className="mt-3">
                <span
                  className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${priorityStyles[request.priority] || ""}`}
                >
                  {t(priorityI18nKeys[request.priority] || "")}
                </span>
              </div>
            </div>

            {/* Available Asset Picker */}
            <div>
              <h3 className="text-sm font-medium text-foreground mb-3">
                {t("assetRequests.availableAssets")}
              </h3>
              {loadingAssets ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground py-3">
                  <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                  {t("common.loading")}
                </div>
              ) : availableAssets.length === 0 ? (
                <div className="text-sm text-muted-foreground bg-muted/50 rounded-lg p-4 text-center">
                  {t("assetRequests.noAvailableAssets")}
                </div>
              ) : (
                <>
                  <select
                    value={selectedAssetId ?? ""}
                    onChange={(e) => {
                      const val = e.target.value ? Number(e.target.value) : null;
                      setSelectedAssetId(val);
                      setOtpGenerated(false);
                      setOtp("");
                      if (errors.asset) setErrors((prev) => ({ ...prev, asset: "" }));
                    }}
                    className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary ${
                      errors.asset ? "border-destructive" : "border-border"
                    }`}
                    disabled={fulfilling}
                    data-testid="fulfill-asset-select"
                  >
                    <option value="">{t("assetRequests.selectAssetPlaceholder")}</option>
                    {availableAssets.map((asset) => (
                      <option key={asset.id} value={asset.id}>
                        {asset.name}
                        {asset.serialNumber ? ` (${asset.serialNumber})` : ""}
                      </option>
                    ))}
                  </select>
                  {errors.asset && <p className="mt-1 text-xs text-destructive">{errors.asset}</p>}
                </>
              )}
            </div>

            {/* Condition Assessment */}
            <div>
              <h3 className="text-sm font-medium text-foreground mb-3">
                {t("assetRequests.conditionAtIssue")}
              </h3>
              <select
                value={condition}
                onChange={(e) => setCondition(e.target.value as AssetCondition)}
                className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                disabled={fulfilling}
                data-testid="fulfill-condition-select"
              >
                {[AssetCondition.Excellent, AssetCondition.Good, AssetCondition.Fair, AssetCondition.Poor].map((c) => (
                  <option key={c} value={c}>{conditionLabel(c)}</option>
                ))}
              </select>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                {t("assetRequests.fulfillNotes")}
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                placeholder={t("assetRequests.fulfillNotesPlaceholder")}
                disabled={fulfilling}
                data-testid="fulfill-notes-input"
              />
            </div>

            {/* OTP Verification */}
            {requireOtp && (
              <div>
                <h3 className="text-sm font-medium text-foreground mb-3">
                  {t("assetRequests.otpVerification")}
                </h3>
                <div className="bg-primary/10 border border-primary/20 rounded-lg p-3 mb-3">
                  <div className="flex items-start gap-2">
                    <svg className="w-4 h-4 text-primary mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div>
                      <p className="text-sm font-medium text-primary">{t("assets.otpVerificationRequired")}</p>
                      <p className="text-xs text-primary mt-0.5">{t("assetRequests.generateOtpForFulfill")}</p>
                    </div>
                  </div>
                </div>

                <div className="flex items-end gap-3 mb-3">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-foreground mb-1">
                      {t("assets.generateOtp")}
                    </label>
                    <Button
                      type="button"
                      onClick={handleGenerateOtp}
                      disabled={selectedAssetId == null || fulfilling}
                      loading={generatingOtp}
                      data-testid="fulfill-generate-otp-btn"
                    >
                      {!generatingOtp && (
                        <svg className="w-4 h-4 me-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                        </svg>
                      )}
                      {generatingOtp ? t("assets.generating") : t("assets.generateOtp")}
                    </Button>
                  </div>
                </div>

                {selectedAssetId == null && (
                  <p className="text-xs text-muted-foreground mb-2">{t("assetRequests.selectAsset")}</p>
                )}
                {otpGenerated && (
                  <p className="text-xs text-success font-medium mb-2">{t("assets.otpSent")}</p>
                )}

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    {t("assets.otp")} *
                  </label>
                  <input
                    type="text"
                    value={otp}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, "").slice(0, 6);
                      setOtp(val);
                      if (errors.otp) setErrors((prev) => ({ ...prev, otp: "" }));
                    }}
                    maxLength={6}
                    className={`w-full px-3 py-2 border rounded-lg text-sm font-mono tracking-widest focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary ${
                      errors.otp ? "border-destructive" : "border-border"
                    }`}
                    placeholder="000000"
                    disabled={fulfilling}
                    data-testid="fulfill-otp-input"
                  />
                  {errors.otp && <p className="mt-1 text-xs text-destructive">{errors.otp}</p>}
                  <p className="mt-1 text-xs text-muted-foreground">{t("assets.otpHint")}</p>
                </div>
              </div>
            )}
          </DialogBody>

          {/* Actions */}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={fulfilling}
            >
              {t("common.cancel")}
            </Button>
            <Button
              type="submit"
              loading={fulfilling}
              data-testid="fulfill-submit-btn"
            >
              {fulfilling ? t("assetRequests.processing") : (
                <>
                  <svg className="w-4 h-4 me-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {t("assetRequests.fulfill")}
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default FulfillAssetRequestModal;
